import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CANONICAL_BASE = 'https://www.pizzerialatorrecarmagnola.it';

interface PageSEOProps {
  title: string;
  description: string;
  noindex?: boolean;
}

const PageSEO = ({ title, description, noindex = false }: PageSEOProps) => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = title;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const canonicalUrl = `${CANONICAL_BASE}${pathname === '/' ? '/' : pathname}`;
    if (canonical) {
      canonical.href = canonicalUrl;
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = canonicalUrl;
      document.head.appendChild(canonical);
    }

    // Helper: upsert <meta property="..."> tags
    const upsertMeta = (attr: 'property' | 'name', key: string, value: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    // Robots (noindex per-route)
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');

    return () => {
      // Reset to indexable default on unmount so SPA navigation doesn't leak noindex
      const r = document.querySelector('meta[name="robots"]');
      if (r) r.setAttribute('content', 'index, follow');
    };
  }, [title, description, pathname, noindex]);

  return null;
};

export default PageSEO;
